/**
 * Shared utilities for image operations in the browser
 */

// Shared constant for image/diagram preview height
export const MEDIA_PREVIEW_HEIGHT = 180

export interface CopyResult {
  success: boolean
  error?: string
}

/**
 * Convert a canvas to PNG blob
 */
async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}

/**
 * Copy image blob to clipboard
 */
async function copyBlobToClipboard(blob: Blob): Promise<void> {
  await navigator.clipboard.write([
    new ClipboardItem({ [blob.type]: blob })
  ])
}

/**
 * Convert image to PNG via canvas and copy to clipboard
 */
async function convertAndCopyViaCanvas(
  source: HTMLImageElement | HTMLCanvasElement,
  width: number,
  height: number,
  backgroundColor?: string
): Promise<CopyResult> {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return { success: false, error: 'Failed to get canvas context' }
  }

  if (backgroundColor) {
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)
  }

  ctx.drawImage(source, 0, 0, width, height)

  const pngBlob = await canvasToPngBlob(canvas)
  if (!pngBlob) {
    return { success: false, error: 'Failed to create PNG blob' }
  }

  await copyBlobToClipboard(pngBlob)
  return { success: true }
}

/**
 * Copy image from URL to clipboard
 * Supports both regular URLs and data URLs
 */
export async function copyImageToClipboard(url: string): Promise<CopyResult> {
  try {
    // For data URLs, decode directly without fetch
    if (url.startsWith('data:')) {
      const [header, base64Data] = url.split(',')
      if (!base64Data) {
        return { success: false, error: 'Invalid data URL' }
      }

      const mimeMatch = header.match(/data:([^;]+)/)
      const mimeType = mimeMatch?.[1] || 'image/png'

      // Decode base64 to binary
      const binaryString = atob(base64Data)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const blob = new Blob([bytes], { type: mimeType })

      // If it's already PNG or JPEG, copy directly
      if (mimeType === 'image/png' || mimeType === 'image/jpeg') {
        await copyBlobToClipboard(blob)
        return { success: true }
      }

      // Convert other formats via canvas
      const img = new window.Image()
      const objectUrl = URL.createObjectURL(blob)

      return new Promise((resolve) => {
        img.onload = async () => {
          URL.revokeObjectURL(objectUrl)
          try {
            const result = await convertAndCopyViaCanvas(img, img.width, img.height)
            resolve(result)
          } catch {
            resolve({ success: false, error: 'Failed to convert image' })
          }
        }
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          resolve({ success: false, error: 'Failed to load image' })
        }
        img.src = objectUrl
      })
    }

    // For regular URLs, fetch and copy
    const response = await fetch(url)
    const blob = await response.blob()

    // If it's already PNG or JPEG, copy directly
    if (blob.type === 'image/png' || blob.type === 'image/jpeg') {
      await copyBlobToClipboard(blob)
      return { success: true }
    }

    // Convert other formats via canvas
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    const objectUrl = URL.createObjectURL(blob)

    return new Promise((resolve) => {
      img.onload = async () => {
        URL.revokeObjectURL(objectUrl)
        try {
          const result = await convertAndCopyViaCanvas(img, img.width, img.height)
          resolve(result)
        } catch {
          resolve({ success: false, error: 'Failed to convert image' })
        }
      }
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        resolve({ success: false, error: 'Failed to load image' })
      }
      img.src = objectUrl
    })
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Copy SVG as PNG image to clipboard
 */
export async function copySvgAsImage(
  svgString: string,
  options?: { scale?: number; backgroundColor?: string }
): Promise<CopyResult> {
  const { scale = 2, backgroundColor } = options || {}

  try {
    // Parse SVG to get dimensions
    const parser = new DOMParser()
    const doc = parser.parseFromString(svgString, 'image/svg+xml')
    const svgElement = doc.querySelector('svg')

    if (!svgElement) {
      return { success: false, error: 'Invalid SVG' }
    }

    // Get original dimensions
    const widthAttr = svgElement.getAttribute('width')
    const heightAttr = svgElement.getAttribute('height')
    const viewBox = svgElement.getAttribute('viewBox')

    let width = 800, height = 600
    if (widthAttr && heightAttr) {
      width = parseFloat(widthAttr) || 800
      height = parseFloat(heightAttr) || 600
    } else if (viewBox) {
      const parts = viewBox.split(' ')
      if (parts.length === 4) {
        width = parseFloat(parts[2]) || 800
        height = parseFloat(parts[3]) || 600
      }
    }

    // Scale up for better quality
    width *= scale
    height *= scale

    // Create canvas
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return { success: false, error: 'Failed to get canvas context' }
    }

    // Fill background if specified
    if (backgroundColor) {
      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, width, height)
    }

    // Create image from SVG
    const img = new window.Image()
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const objectUrl = URL.createObjectURL(svgBlob)

    return new Promise((resolve) => {
      img.onload = async () => {
        ctx.drawImage(img, 0, 0, width, height)
        URL.revokeObjectURL(objectUrl)

        try {
          const pngBlob = await canvasToPngBlob(canvas)
          if (pngBlob) {
            await copyBlobToClipboard(pngBlob)
            resolve({ success: true })
          } else {
            resolve({ success: false, error: 'Failed to create PNG' })
          }
        } catch {
          resolve({ success: false, error: 'Failed to copy to clipboard' })
        }
      }
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        resolve({ success: false, error: 'Failed to load SVG' })
      }
      img.src = objectUrl
    })
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Extract image format from URL or data URL
 */
export function getImageFormat(src: string): string {
  // Data URL: data:image/png;base64,...
  if (src.startsWith('data:image/')) {
    const match = src.match(/^data:image\/(\w+)/)
    if (match) {
      const format = match[1].toUpperCase()
      if (format === 'JPEG' || format === 'JPG') return 'JPEG'
      if (format === 'SVG+XML') return 'SVG'
      return format
    }
  }

  // Regular URL: check extension
  const extMatch = src.match(/\.(\w+)(?:\?.*)?$/)
  if (extMatch) {
    const ext = extMatch[1].toUpperCase()
    if (ext === 'JPG' || ext === 'JPEG') return 'JPEG'
    if (ext === 'PNG' || ext === 'GIF' || ext === 'WEBP' || ext === 'SVG') return ext
  }

  return 'Image'
}

/**
 * Normalize data URL by removing whitespace
 * (markdown can have linebreaks in data URLs)
 */
export function normalizeDataUrl(url: string): string {
  if (url.startsWith('data:')) {
    return url.replace(/\s/g, '')
  }
  return url
}
